import { TYPE_PAIRS, TYPE_PAIR_FONTS } from '@tallyframe/core';
/**
 * CSS `@page` margin-box rules for the printed document's running header,
 * footer and brand bar (spec §8.3, redesigned in the theming spec §2.1-§2.4).
 *
 * Rendered into a runtime `<style>` element by `PrintReport`, never into a
 * print.css partial: the brand mark is per-report data, and
 * `packages/renderer/test/styles-bundle.test.ts` forbids a hard-coded hex
 * literal anywhere outside `tokens.css`.
 *
 * **Amended 2026-09-22 after the PDF spike** (`apps/server/test/pdf-margin-boxes.test.ts`,
 * `apps/server/test/fixtures/margin-boxes.html`), confirmed in the bundled
 * Chromium:
 *  - A `position: fixed` bar with a negative `top` offset does not work — a
 *    negative `top` on a fixed element lands it near the *bottom* of the page
 *    during paginated printing, not the top. Margin boxes have no such offset
 *    to get wrong, so the bar is drawn instead as a `border-top` repeated on
 *    all five top margin boxes (`@top-left-corner`, `@top-left`,
 *    `@top-center`, `@top-right`, `@top-right-corner`), giving an edge-to-edge
 *    bar on every page.
 *  - `@page :first` must set the header boxes' `content` to `""`, never
 *    `none`: an empty string keeps the box (and its `border-top`) alive so the
 *    bar still draws on page 1, where `none` would remove the box entirely
 *    and break the bar.
 */
/** Quotes a CSS `content` string value, escaping backslash, double quote and
 *  every line-break character (CRLF, CR, LF and form feed, all as `\A `, the
 *  CSS string escape for one) — inputs are user-authored report titles and
 *  brand/operator names, never HTML, so no other character needs escaping.
 *  An unescaped line break of any of those four kinds inside a CSS string is
 *  a bad-string token: Chromium drops the whole `content` declaration it
 *  sits in, so the box's text (and only that box's text — the bar and the
 *  other boxes are unaffected) silently disappears. Order matters:
 *  backslashes are escaped first so the backslashes introduced by
 *  quote-escaping are never themselves re-escaped. */
export function cssString(value) {
    const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r\n|\r|\n|\f/g, '\\A ');
    return `"${escaped}"`;
}
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
/** The published-links badge, in the `@bottom-center` slot that slice owns.
 *  `BOX_SHARES.bottomCenter` is sized for this exact string (spike item 6). */
export const BADGE_TEXT = 'Made with ReportPad · reportpad.app';
/**
 * Each margin box's share of the page content width. Exported so PrintReport
 * fits each string to its own box. Each band sums to 1.
 */
// spike fallback: item 6 — @bottom-center widened from 25% to 32%, the sides
// narrowed from 40%/35% to 34%/34%. The slice 3 badge ("Made with ReportPad ·
// reportpad.app") wrapped to two lines in a 25% box in all four type pairs:
// 25% of A4 portrait's 508.88pt content width is 127.2pt, and one line needs
// about 139-152pt (~30%). 32% is 162.8pt, about 10pt clear of the widest pair.
// A4 portrait is the narrowest page this product prints, so every other page
// size has more room.
//
// The sides are equal because Chromium centres @bottom-center on the content
// width rather than packing it after @bottom-left (spike item 1). With 40/25/35
// the centre box sat at 37.5% and overlapped the left box by 2.5%; with equal
// sides the three boxes tile exactly.
export const BOX_SHARES = {
    topLeft: 0.5,
    topRight: 0.5,
    bottomLeft: 0.34,
    bottomCenter: 0.32,
    bottomRight: 0.34
};
const BODY_STACKS = TYPE_PAIRS.map((pair) => TYPE_PAIR_FONTS[pair].body);
/** `0.34` -> `34%`, without the float noise of `0.34 * 100`. */
function width(share) {
    return `width: ${Math.round(share * 1000) / 10}%;`;
}
/**
 * The running header and footer.
 *
 * `title`, `operator` and `footerText` arrive already fitted to their boxes
 * and go through `cssString`, so any quoted content is safe. `brandMark` and
 * `fontStack` are interpolated as bare declaration values, where a stray `}`
 * would close the rule and let the rest run as arbitrary CSS, so both are
 * checked rather than trusted: `brandMark` must be a 6-digit hex (else black),
 * and `fontStack` must be one of the type-pair body stacks (else Plex).
 * `resolveBrand` yields a hex today, but `deriveBrandColors` passes most
 * accents through unchanged, so nothing upstream re-validates it.
 *
 * Text boxes use `var(--rp-font)` and `var(--rp-muted)`, resolved from
 * `:root`: a margin box is not a descendant of `.tf-print`, so it sees only
 * the `:root` mirror in tokens.css plus the `:root` font override emitted
 * here. Without a resolvable family a real PDF once printed its header and
 * footer in a Times New Roman fallback.
 *
 * `@page :first` empties the header text with `content: ""`, never `none`:
 * `none` removes the box and its `border-top`, which breaks the brand bar on
 * page 1. `:first` is the literal first page, not "the cover page"; if the
 * key figures share page 1 with the cover, the cover's own brand line and
 * title already orient the reader.
 */
export function pageBandsCss(input) {
    const brandMark = HEX_COLOR.test(input.brandMark) ? input.brandMark : '#000000';
    const fontStack = BODY_STACKS.includes(input.fontStack) ? input.fontStack : TYPE_PAIR_FONTS.plex.body;
    // Margin boxes inherit from the page, which inherits from <html>: line-height is set here so a host
    // page's reset (Tailwind preflight sets html line-height 1.5) cannot move the header and footer.
    const text = 'font-size: 8.5pt; line-height: normal; color: var(--rp-muted); font-family: var(--rp-font); vertical-align: middle;';
    const bar = `border-top: 6px solid ${brandMark};`;
    const badge = input.badge ? cssString(BADGE_TEXT) : '""';
    return `:root { --rp-font: ${fontStack}; }
  @page {
    @top-left-corner { content: ""; ${bar} }
    @top-center { content: ""; ${bar} }
    @top-right-corner { content: ""; ${bar} }
    @top-left { content: ${cssString(input.title)}; ${width(BOX_SHARES.topLeft)} ${bar} ${text} }
    @top-right { content: ${cssString(input.operator)}; ${width(BOX_SHARES.topRight)} text-align: right; ${bar} ${text} }
    @bottom-left { content: ${cssString(input.footerText)}; ${width(BOX_SHARES.bottomLeft)} ${text} }
    @bottom-center { content: ${badge}; ${width(BOX_SHARES.bottomCenter)} ${text} }
    @bottom-right { content: "Page " counter(page) " of " counter(pages); ${width(BOX_SHARES.bottomRight)} text-align: right; ${text} }
  }
  @page :first {
    @top-left { content: ""; }
    @top-right { content: ""; }
  }`;
}
//# sourceMappingURL=print-bands.js.map